import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import * as styles from '../SideBySideVideo.module.scss'

import { BaseVideoConfig, CampaignConfig } from '../config'
import { getStoredEngine, render, willUseScalar } from '../lib/engines'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { TrackFn } from '../SideBySideVideoPage'
import StepHeader from './StepHeader'
import { DownloadIcon, PlayIcon, Share2Icon } from 'lucide-react'

type Props = {
  config: CampaignConfig
  selected: BaseVideoConfig
  userVideo: Blob
  onBack: () => void
  onReset: () => void
  onDownloaded?: () => void
  // Fired once the step reaches a terminal state (video ready or render failed)
  // so the break can mark the step complete and reveal the next-step arrow.
  onReady?: () => void
  nextStepButton?: React.ReactNode
  onEvent: TrackFn
}

const DownloadStage = ({
  config,
  selected,
  userVideo,
  onBack,
  onReset,
  onDownloaded,
  onReady,
  nextStepButton,
  onEvent,
}: Props) => {
  const { copy } = config
  const { t } = useTranslation('sideBySideVideo')
  const isMobile = !useMediaQuery('(min-width: 768px)')
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const startedRef = useRef(false)
  const awardedRef = useRef(false)
  const readyFiredRef = useRef(false)

  // Mark the step complete once (reveals the next-step arrow). Called when the
  // video is ready, and also on failure so a failed render doesn't trap the
  // user on the last step.
  const fireReady = () => {
    if (readyFiredRef.current) return
    readyFiredRef.current = true
    onReady?.()
  }

  // Fire engagement/points at most once per session — guards against repeated
  // download clicks farming points.
  const handleDownloadClick = () => {
    onEvent('step_interaction_sbsv_download')
    if (awardedRef.current) return
    awardedRef.current = true
    onDownloaded?.()
  }

  const [output, setOutput] = useState<Blob | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState(t('download.stage.preparing'))
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)

  // The scalar (single-thread) ffmpeg path blocks the main thread during the
  // encode, so a % bar can't update — show an indeterminate spinner instead.
  const [scalarPath] = useState(() => willUseScalar())

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    let cancelled = false

    const run = async () => {
      const chosen = getStoredEngine()
      // The engine that actually runs may differ (e.g. Firefox falls back from
      // mediabunny to ffmpeg). render() reports it via onEngine — log/report that.
      let actualEngine: string = chosen
      const startedAt = performance.now()
      try {
        setStage(
          chosen === 'mediabunny'
            ? t('download.stage.readingInputs')
            : t('download.stage.preparing'),
        )
        const out = await render({
          baseVideo: selected,
          userBlob: userVideo,
          overlay: null,
          overlayImageSrc: config.branding.overlayImageSrc,
          layout: config.layout,
          durationSeconds: selected.durationSeconds,
          onEngine: e => {
            actualEngine = e
          },
          onProgress: (r, phase) => {
            if (cancelled) return
            const label =
              phase === 'normalize'
                ? t('download.stage.creating')
                : phase === 'composite'
                ? t('download.stage.creating')
                : phase === 'fetching'
                ? t('download.stage.preparing')
                : phase === 'reading'
                ? t('download.stage.preparing')
                : phase === 'audio'
                ? t('download.stage.almostThere')
                : phase === 'finalize'
                ? t('download.stage.almostThere')
                : t('download.stage.creating')
            setStage(label)
            setProgress(r)
          },
          onLog: msg => {
            // eslint-disable-next-line no-console
            console.log('[' + actualEngine + ']', msg)
          },
        })
        if (cancelled) return
        onEvent('step_interaction_sbsv_render_complete', {
          engine: actualEngine,
          duration_ms: Math.round(performance.now() - startedAt),
        })
        setOutput(out)
        const u = URL.createObjectURL(out)
        setUrl(u)
        fireReady()
      } catch (e) {
        if (cancelled) return
        // Surface the real cause in the console — the UI message is generic, so
        // without this a render failure is undiagnosable from a device log.
        // eslint-disable-next-line no-console
        console.error('[sbv] render failed', { engine: actualEngine }, e)
        onEvent('step_interaction_sbsv_render_failed', {
          engine: actualEngine,
          error: (e as Error)?.message ?? 'unknown',
        })
        setError(t('download.error'))
        fireReady()
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [selected, userVideo])

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [url])

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) {
      v.play()
      setPlaying(true)
    } else {
      v.pause()
      setPlaying(false)
    }
  }

  // Web Share API L2 — the only way to share the actual MP4 file from the
  // browser. Opens the OS share sheet with the video attached; the user picks
  // the destination app (Instagram, TikTok, WhatsApp, etc.). Mobile only.
  const buildShareFile = () =>
    output ? new File([output], 'side-by-side.mp4', { type: 'video/mp4' }) : null

  const canShareVideo =
    isMobile &&
    typeof navigator !== 'undefined' &&
    !!output &&
    !!navigator.canShare &&
    (() => {
      const f = buildShareFile()
      return f ? navigator.canShare({ files: [f] }) : false
    })()

  const handleShareVideo = async () => {
    const file = buildShareFile()
    if (!file) return
    onEvent('step_interaction_sbsv_share')
    const shareData: ShareData = {
      files: [file],
      title: copy.shareTitle || copy.downloadTitle,
      text: copy.shareText || copy.downloadSubtitle,
    }
    if (copy.shareUrl) shareData.url = copy.shareUrl
    try {
      await navigator.share(shareData)
    } catch {
      // user dismissed the share sheet — no-op
    }
  }

  const ready = !!output && !!url

  return (
    <>
      <StepHeader step={4} total={4} onBack={onBack} />

      <div style={{ width: '100%' }}>
        <h2 className={styles.title}>{copy.downloadTitle}</h2>
        <p className={styles.subtitle}>{copy.downloadSubtitle}</p>
      </div>

      {!ready && !error && (
        scalarPath ? (
          <>
            <div className={styles.spinner} role="status" aria-label={stage} />
            <p className={styles.hint}>{stage}</p>
            <p className={styles.hint}>{t('download.slowDeviceHint')}</p>
          </>
        ) : (
          <>
            <p className={styles.hint}>{stage}</p>
            <div className={styles.progress}>
              <div
                className={styles.progressBar}
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <p className={styles.hint}>{Math.round(progress * 100)}%</p>
          </>
        )
      )}

      {error && <p className={styles.error}>{error}</p>}

      {ready && url && (
        <>
          <div
            className={[
              styles.output,
              config.layout === 'stacked' ? styles.outputStacked : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <video
              ref={videoRef}
              src={url}
              playsInline
              onClick={togglePlay}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />
            {!playing && (
              <button
                type="button"
                className={`${styles.outputOverlay} ${styles.outputOverlayBtn}`}
                onClick={togglePlay}
                aria-label={t('download.watchNow')}
              >
                <PlayIcon size={20} /> {t('download.watchNow')}
              </button>
            )}
          </div>

          <div
            className={
              config.layout === 'stacked'
                ? styles.outputMetaStacked
                : styles.outputMeta
            }
          >
            <p className={styles.addLabel} style={{ marginBottom: 0 }}>
              {t('download.downloadLabel')}
            </p>

            <a
              href={url}
              download="side-by-side.mp4"
              className={styles.btn}
              onClick={handleDownloadClick}
            >
              {copy.downloadCta} <DownloadIcon size={20} />
            </a>

            {canShareVideo && (
              <>
                <div className={styles.divider} />

                <p className={styles.addLabel} style={{ marginBottom: 0 }}>
                  {t('download.shareToSocialLabel')}
                </p>

                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={handleShareVideo}
                >
                  {t('download.shareVideo')} <Share2Icon size={20} />
                </button>
              </>
            )}
          </div>
        </>
      )}

      <button
        type="button"
        className={styles.uploadLink}
        onClick={() => {
          onEvent('step_interaction_sbsv_start_over')
          onReset()
        }}
      >
        {t('download.startOver')}
      </button>

      {/* Break's next-step arrow — appears once the parent marks the step
          complete (via onReady → onEngage). Matches other step types. */}
      {nextStepButton}
    </>
  )
}

export default DownloadStage
